import React, { useState, useEffect } from 'react';
import { Send, Users, Crown, Shield, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
  { id: 'all', name: 'All Users', icon: Users, description: 'Send to everyone' },
  { id: 'premium', name: 'Premium Users', icon: Crown, description: 'Only premium members' },
  { id: 'verified', name: 'Verified Users', icon: Shield, description: 'Only verified users' },
  { id: 'male', name: 'Male Users', icon: Users, description: 'Only male users' },
  { id: 'female', name: 'Female Users', icon: Users, description: 'Only female users' },
];

const AdminBulkMessaging = () => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    target_audience: 'all',
    send_email: true,
    send_notification: true
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/bulk-messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.messages || []);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!formData.subject || !formData.message) {
      toast({ title: "Error", description: "Please fill subject and message", variant: "destructive" });
      return;
    }

    if (!formData.send_email && !formData.send_notification) {
      toast({ title: "Error", description: "Select at least one delivery method", variant: "destructive" });
      return;
    }

    if (!window.confirm(`Send this message to ${AUDIENCES.find(a => a.id === formData.target_audience)?.name}?`)) {
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/bulk-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: "Message Sent!", 
          description: `Sent to ${data.recipients_count} users` 
        });
        setFormData(p => ({ ...p, subject: '', message: '' }));
        loadHistory();
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send message", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Bulk Messaging</h1>
        <p className="text-gray-600">Send messages to multiple users at once</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Compose Message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Target Audience */}
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {AUDIENCES.map((audience) => (
                    <button
                      key={audience.id}
                      onClick={() => setFormData(p => ({ ...p, target_audience: audience.id }))}
                      className={`flex items-center gap-2 p-3 border rounded-lg text-left transition-colors ${
                        formData.target_audience === audience.id
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      <audience.icon className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium text-sm">{audience.name}</p>
                        <p className="text-xs text-gray-500">{audience.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Message subject..."
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label>Message</Label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                  placeholder="Your message to users..."
                  className="w-full h-40 p-3 border rounded-lg resize-none"
                />
              </div>

              {/* Delivery Options */}
              <div className="flex gap-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.send_notification}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, send_notification: c }))}
                  />
                  <Label>In-App Notification</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.send_email}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, send_email: c }))}
                  />
                  <Label>Email</Label>
                </div>
              </div>

              {/* Send Button */}
              <Button 
                onClick={handleSend} 
                disabled={sending} 
                className="w-full bg-pink-500 hover:bg-pink-600 text-white"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Message</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* History */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Recent Messages</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {loading ? (
                <div className="flex justify-center p-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : history.length === 0 ? (
                <p className="text-center text-gray-500 p-4 text-sm">No messages sent yet</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.map((msg) => (
                    <div key={msg.id} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between">
                        <p className="font-medium text-sm truncate flex-1">{msg.subject}</p>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{msg.message}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <Users className="w-3 h-3" />
                        <span>{msg.recipients_count} recipients</span>
                        <span>•</span>
                        <Clock className="w-3 h-3" />
                        <span>{new Date(msg.sent_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminBulkMessaging;
